import { useSuspenseTagQuery } from '#api/tags.queries.ts';
import type { TagName } from '#dn-types/host.ts';
import { mapTagToForm } from '../form/mapTagToForm.ts';
import { TagForm } from '../form/TagForm.tsx';

type Props = {
  /** The tag to copy config from */
  sourceTagName: TagName;
  onSuccess: () => void;
};

/**
 * {@link DuplicateTagForm} loads the source tag and renders the add-tag form prefilled with its
 * description, config overrides, route subscriptions, and firewall rules.  The user picks a new
 * name; hosts are not copied.
 *
 * The source tag is only needed for the initial prefill: once the form mounts, its state is
 * independent, and creating the copy succeeds even if the source is deleted before saving.  So
 * unlike editing, a background refetch 404 must not be escalated — that would destroy the user's
 * in-progress form.
 */
export function DuplicateTagForm({ sourceTagName, onSuccess }: Props) {
  const {
    data: { data: sourceTag },
  } = useSuspenseTagQuery(sourceTagName);

  return (
    <TagForm
      // The form only reads defaultValues on first mount, so re-mount it if the source changes
      key={sourceTag.name}
      tagName={null}
      hostCount={null}
      duplicateOf={sourceTag.name}
      defaultValues={{ ...mapTagToForm(sourceTag), name: '' }}
      onSuccess={onSuccess}
    />
  );
}
